import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  ChangeDetectionStrategy,
} from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { form, FormField, FormRoot, minLength, required } from "@angular/forms/signals";
import { BrandAsset, DspDisplayCreativeCreateRequest, MediaType } from "@front/m19-api-client";
import { DspStrategyEx } from "@front/m19-models";
import {
  AccountSelectionService,
  DspCreativeAssetsService,
  DspCreativeService,
  DspStrategyService,
} from "@front/m19-services";
import {
  IButtonDirective,
  IFormFieldComponent,
  IIconComponent,
  IInputDirective,
  ILabelComponent,
  MODAL_DATA,
  ModalRef,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { AsinSelectionComponent } from "@m19-board/shared/asin-selection/asin-selection.component";
import {
  MediaPreviewModalComponent,
  MediaPreviewModalData,
} from "@m19-board/shared/media/media-preview-modal/media-preview-modal.component";
import {
  MediaSelectionModalComponent,
  MediaSelectionModalData,
  MediaSelectionModalResult,
} from "@m19-board/shared/media/media-selection-modal/media-selection-modal.component";
import { switchMap, take } from "rxjs";
import { RECOMMENDED_IMAGE_SIZES } from "../dsp-custom-creatives-table/dsp-custom-creatives-lines.component";
import { Media } from "@m19-board/shared/media/media";

interface DspDisplayCreativeFormModel {
  name: string;
  assets: Media[];
  asin: string | null;
}

export interface DspCustomCreativeCreationModalData {
  strategy: DspStrategyEx;
  isReadOnly: boolean;
}

@Component({
  selector: "app-dsp-custom-creative-creation-modal",
  imports: [
    FormRoot,
    IFormFieldComponent,
    ILabelComponent,
    TranslocoDirective,
    IInputDirective,
    FormField,
    AsinSelectionComponent,
    IButtonDirective,
    IIconComponent,
  ],
  templateUrl: "./dsp-custom-creative-creation-modal.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DspCustomCreativeCreationModalComponent {
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly dspCreativeAssetsService = inject(DspCreativeAssetsService);
  private readonly modalService = inject(ModalService);
  private readonly translocoService = inject(TranslocoService);
  private readonly dspCreativeService = inject(DspCreativeService);
  private readonly toasterService = inject(ToasterService);
  private readonly dspStrategyService = inject(DspStrategyService);
  private readonly destroyRef = inject(DestroyRef);

  protected modalData = inject<DspCustomCreativeCreationModalData>(MODAL_DATA);
  protected modalRef = inject(ModalRef);

  readonly recommendedImageSizes = RECOMMENDED_IMAGE_SIZES;
  readonly otherImageMediaType = MediaType.otherImage;
  readonly MAX_CREATIVE_ASSETS = 5;

  private readonly dspCreativeAssets = toSignal(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) =>
        this.dspCreativeAssetsService.getDspCreativeAssets(am.accountId, am.marketplace, am.dspAdvertiserId!),
      ),
    ),
  );

  readonly formModel = signal<DspDisplayCreativeFormModel>({
    name: "",
    assets: [],
    asin: null,
  });

  readonly creationForm = form(
    this.formModel,
    (schema) => {
      required(schema.name);
      required(schema.assets);
      minLength(schema.assets, 1, { message: "dsp-custom-creative-creation.at_least_one_asset_is_required" });
      required(schema.asin, { message: "dsp-custom-creative-creation.product_required" });
    },
    {
      submission: {
        action: async () => {
          this.createCreative();
        },
      },
    },
  );

  readonly formSubmitting = signal(false);
  readonly uploadingAsset = signal(false);

  readonly customProductList = this.modalData.strategy.asins.map((a) => a.asin);

  readonly selectedAsins = computed(() =>
    this.creationForm.asin().value() ? [this.creationForm.asin().value()!] : [],
  );

  readonly canAddAsset = computed(() => this.creationForm.assets().value().length < this.MAX_CREATIVE_ASSETS);

  constructor() {
    afterNextRender(() => {
      this.creationForm().focusBoundControl();
    });
  }

  createCreative() {
    if (!this.creationForm().valid()) {
      return;
    }

    this.formSubmitting.set(true);
    const assetIds = this.creationForm
      .assets()
      .value()
      .map((a) => a.assetId!)
      .filter((a) => a !== undefined);

    const creationRequest: DspDisplayCreativeCreateRequest = {
      name: this.creationForm.name().value(),
      assetIds,
      asin: this.creationForm.asin().value()!,
    };

    this.dspCreativeService
      .createDspDisplayCreative(creationRequest)
      .pipe(
        switchMap((creatives) => this.dspStrategyService.attachCreativeToStrategy(this.modalData.strategy, creatives)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (_) => {
          this.toasterService.success(
            this.translocoService.translate("dsp-custom-creative-creation.x_creatives_created", {
              count: assetIds.length,
            }),
          );
          this.modalRef.close(true);
        },
        error: (error) => {
          this.formSubmitting.set(false);
          this.toasterService.error(
            this.translocoService.translate("dsp-custom-creative-creation.creative_creation_error"),
            error.message,
          );
        },
      });
  }

  selectedAsinsChanged(asin: string[]) {
    this.creationForm.asin().value.set(asin[0] ?? null);
  }

  openMediaSelectionModal() {
    if (!this.canAddAsset()) {
      return;
    }
    this.modalService
      .openModal<MediaSelectionModalData, MediaSelectionModalResult<BrandAsset>>(MediaSelectionModalComponent, {
        modalTitle: this.translocoService.translate("media-input.select_a_file"),
        data: {
          mediaType: this.otherImageMediaType,
          medias: this.dspCreativeAssets() ?? [],
          imageSizeFilterValues: RECOMMENDED_IMAGE_SIZES,
        },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        if (result.newFile) {
          this.uploadAsset(result.newFile);
        } else if (result.selectedMedia) {
          this.creationForm.assets().value.set([...this.creationForm.assets().value(), result.selectedMedia]);
        }
      });
  }

  removeAsset(assetId: string | undefined) {
    if (!assetId) {
      return;
    }
    this.creationForm.assets().value.set(
      this.creationForm
        .assets()
        .value()
        .filter((a) => a.assetId !== assetId),
    );
  }

  expandAsset(asset: Media) {
    this.modalService.openModal<MediaPreviewModalData, void>(MediaPreviewModalComponent, {
      data: {
        mediaSource: asset.url!,
        mediaType: asset.mediaType,
      },
    });
  }

  private uploadAsset(file: File) {
    this.uploadingAsset.set(true);
    this.accountSelectionService.singleAccountMarketplaceSelection$
      .pipe(
        take(1),
        switchMap((am) =>
          this.dspCreativeAssetsService.uploadCreativeAsset(
            am.accountId,
            am.marketplace,
            am.dspAdvertiserId!,
            file,
            MediaType.otherImage,
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (asset) => {
          this.uploadingAsset.set(false);
          this.creationForm.assets().value.set([...this.creationForm.assets().value(), asset]);
        },
        error: (error) => {
          this.uploadingAsset.set(false);
          this.toasterService.error(error.message);
        },
      });
  }
}
